import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen, Plus, Search, Calendar, Trash2, ChevronLeft, ChevronRight, User, MoreHorizontal, X, Pencil } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import toast from 'react-hot-toast';
import CreateBitacoraModal from './CreateBitacoraModal';
import '../AssetsView.css';

const formatFecha = (fechaStr) => {
  if (!fechaStr) return '';
  const [y, m, d] = fechaStr.split('-');
  const fecha = new Date(Number(y), Number(m) - 1, Number(d));
  return fecha.toLocaleDateString('es-VE', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Lightbox a pantalla completa para ver una imagen ampliada.
const Lightbox = ({ imagenes, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  const multiple = imagenes.length > 1;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
      >
        <X size={22} />
      </button>

      {multiple && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 p-2.5 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 p-2.5 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          >
            <ChevronRight size={26} />
          </button>
        </>
      )}

      <img
        src={imagenes[index]}
        alt={`Imagen ${index + 1}`}
        className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {multiple && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-medium">
          {index + 1} / {imagenes.length}
        </div>
      )}
    </div>,
    document.body
  );
};

// Carrusel deslizable de imágenes de una entrada. Soporta flechas, dots,
// swipe táctil y ampliar a pantalla completa (lightbox).
const EntradaCarrusel = ({ imagenes, alt }) => {
  const [current, setCurrent] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const touchStartX = useRef(null);

  const total = imagenes.length;
  const multiple = total > 1;

  const go = (i) => setCurrent(((i % total) + total) % total);
  const prev = () => go(current - 1);
  const next = () => go(current + 1);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) { delta < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  return (
    <div className="relative select-none">
      <div
        className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100 group"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Pista deslizante */}
        <div
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {imagenes.map((src, i) => (
            <div key={i} className="relative w-full h-full flex-shrink-0 overflow-hidden">
              {/* Fondo difuminado que rellena los bordes (look tipo feed) */}
              <img
                src={src}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl brightness-90 opacity-60"
              />
              {/* Imagen real, contenida y centrada */}
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                className="relative w-full h-full object-contain cursor-zoom-in"
                onClick={() => setLightbox(true)}
                onError={(e) => { e.target.onerror = null; e.target.style.visibility = 'hidden'; }}
              />
            </div>
          ))}
        </div>

        {/* Degradados sutiles para legibilidad de los controles */}
        {multiple && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/25 to-transparent" />
        )}

        {multiple && (
          <>
            {/* Flechas */}
            <button
              onClick={prev}
              className="absolute top-1/2 left-3 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-md backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-105 transition-all"
              aria-label="Anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              className="absolute top-1/2 right-3 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-md backdrop-blur-sm opacity-0 group-hover:opacity-100 hover:bg-white hover:scale-105 transition-all"
              aria-label="Siguiente"
            >
              <ChevronRight size={20} />
            </button>

            {/* Contador */}
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/55 text-white text-[11px] font-semibold backdrop-blur-sm tracking-wide">
              {current + 1} / {total}
            </div>

            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/30 backdrop-blur-sm">
              {imagenes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => go(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === current ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                  }`}
                  aria-label={`Ir a la imagen ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <Lightbox
          imagenes={imagenes}
          index={current}
          onClose={() => setLightbox(false)}
          onPrev={prev}
          onNext={next}
        />
      )}
    </div>
  );
};

const BitacoraCard = ({ entrada, onDelete, onEdit }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <article className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow relative">
      {/* Header */}
      <div className="p-5 pb-3 flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-sura-blue/10 text-sura-blue">
            <BookOpen size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
              <span className="px-2 py-0.5 rounded-md font-medium bg-sura-blue/10 text-sura-blue">Bitácora</span>
              <span>•</span>
              <span className="flex items-center gap-1 capitalize">
                <Calendar size={11} />
                {formatFecha(entrada.fecha)}
              </span>
            </div>
            <p className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
              <User size={11} />
              {entrada.autor_nombre}
            </p>
          </div>
        </div>

        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen(v => !v)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
          >
            <MoreHorizontal size={20} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-20">
                <button
                  onClick={() => { setMenuOpen(false); onEdit(entrada); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Pencil size={14} /> Editar
                </button>
                <button
                  onClick={() => { setMenuOpen(false); setConfirmando(true); }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 size={14} /> Eliminar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="px-5 pt-1 pb-1">
        <h3 className="text-lg font-bold text-gray-900 leading-snug">{entrada.nombre}</h3>
      </div>

      {/* Description */}
      {entrada.descripcion && (
        <div className="px-5 py-2">
          <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{entrada.descripcion}</p>
        </div>
      )}

      {/* Imágenes (carrusel) */}
      {(() => {
        const imgs = entrada.imagenes?.length ? entrada.imagenes : (entrada.image_url ? [entrada.image_url] : []);
        return imgs.length > 0 && <EntradaCarrusel imagenes={imgs} alt={entrada.nombre} />;
      })()}

      <div className="px-5 py-3 border-t border-gray-50 mt-2" />

      {/* Confirm delete overlay */}
      {confirmando && (
        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3 p-4 animate-fadeIn z-30">
          <p className="text-white text-sm font-medium text-center">¿Eliminar esta entrada de la bitácora?</p>
          <div className="flex gap-2">
            <button
              onClick={() => setConfirmando(false)}
              className="px-3 py-1.5 text-xs font-semibold bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => onDelete(entrada.id)}
              className="px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Eliminar
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

const BitacoraView = () => {
  const [entradas, setEntradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntrada, setEditingEntrada] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/bitacora');
      setEntradas(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar la bitácora');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entrada) => {
    setEditingEntrada(entrada);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/api/bitacora/${id}`);
      setEntradas(prev => prev.filter(e => e.id !== id));
      toast.success('Entrada eliminada');
    } catch (error) {
      console.error(error);
      toast.error('Error al eliminar la entrada');
    }
  };

  const entradasFiltradas = useMemo(() => {
    if (!search.trim()) return entradas;
    const term = search.trim().toLowerCase();
    return entradas.filter(e =>
      e.nombre.toLowerCase().includes(term) ||
      (e.descripcion || '').toLowerCase().includes(term)
    );
  }, [entradas, search]);

  return (
    <div className="assets-view">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 m-0">Bitácora</h1>
          <p className="text-gray-400 text-sm m-0 mt-1">Servicios Generales — registro de actividades</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white rounded-xl px-3.5 py-2.5 border border-gray-200 shadow-sm">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar en la bitácora..."
              className="outline-none text-sm text-gray-700 placeholder:text-gray-400 bg-transparent"
            />
          </div>
          <button
            onClick={() => { setEditingEntrada(null); setShowModal(true); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 transition-all"
          >
            <Plus size={16} /> Nueva Bitácora
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sura-blue"></div>
        </div>
      ) : entradasFiltradas.length === 0 ? (
        <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-20 text-center bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 bg-sura-blue/10">
            <BookOpen size={26} className="text-sura-blue" />
          </div>
          <h3 className="text-gray-800 font-semibold mb-1">
            {search ? 'No se encontraron resultados' : 'Aún no hay entradas en la bitácora'}
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            {search ? 'Prueba con otro término de búsqueda' : 'Comienza agregando la primera actividad realizada'}
          </p>
          {!search && (
            <button
              onClick={() => { setEditingEntrada(null); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all bg-sura-blue hover:bg-sura-blue-dark"
            >
              <Plus size={14} /> Nueva Bitácora
            </button>
          )}
        </div>
      ) : (
        <div className="max-w-2xl mx-auto space-y-6">
          {entradasFiltradas.map(entrada => (
            <BitacoraCard key={entrada.id} entrada={entrada} onDelete={handleDelete} onEdit={handleEdit} />
          ))}
        </div>
      )}

      <CreateBitacoraModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingEntrada(null); }}
        onSuccess={fetchData}
        entrada={editingEntrada}
      />
    </div>
  );
};

export default BitacoraView;
